import React, { useState, useEffect } from "react";
import axios from "axios";
import ButtonForm from "../Forms/ButtonForm";
import RichTextForm from "../Forms/RichTextForm";
import { useAppContext } from "../../contexts/AppContext";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

function DashboardForm(Props) {
  const { entries, currentData, onCancel } = Props;
  const { getImageUrl } = useAppContext();
  
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [joinUs, setJoinUs] = useState("");
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [oldImages, setOldImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);

  const apiEndPoint = entries.apiEndPoint;
  const isEditMode = !!currentData;

  useEffect(() => {
    if (currentData) {
      setTitle(currentData.title || currentData.homeTitle || currentData.location || currentData.mobile || currentData.email || "");
      setContent(currentData.content || currentData.homeMessage || currentData.News || currentData.link || "");
      setJoinUs(currentData.joinUs || "");
      setOldImages(currentData.images || []);
      setImages([]);
      setImagePreviews([]);
      setImagesToDelete([]);
    } else {
      setTitle("");
      setContent("");
      setJoinUs("");
      setImages([]);
      setImagePreviews([]);
      setOldImages([]);
      setImagesToDelete([]);
    }
  }, [currentData, entries.content]);

  const handleImageChange = (e) => {
    const files = e.target.files;
    const fileArray = Array.from(files);
    setImages((prevImages) => [...prevImages, ...fileArray]);
    const previews = fileArray.map((file) => URL.createObjectURL(file));
    setImagePreviews((prevPreviews) => [...prevPreviews, ...previews]);
  };

  const handleNewImageDelete = (index) => {
    setImages((prevImages) => prevImages.filter((_, i) => i !== index));
    setImagePreviews((prevPreviews) => prevPreviews.filter((_, i) => i !== index));
  };

  const handleOldImageDelete = (imagePath) => {
    setOldImages((prev) => prev.filter((img) => img !== imagePath));
    setImagesToDelete((prev) => [...prev, imagePath]);
  };

  const onDragEndImages = (result) => {
    if (!result.destination) return;
    const items = Array.from(oldImages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setOldImages(items);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Determine if we should use FormData or JSON
    const useFormData = !!entries.photo;
    let payload;

    if (useFormData) {
      payload = new FormData();
      if (entries.content === "Home body") {
        payload.append("homeTitle", title);
        payload.append("homeMessage", content);
        payload.append("joinUs", joinUs);
      } else if (entries.content === "news") {
        payload.append("News", content);
        payload.append("user", "Admin");
      } else {
        payload.append("title", title);
        // Handle fields that should be sent as 'link' in the backend
        if (["Social media link", "partner website", "Company youtube Account", "portfolio link"].includes(entries.content)) {
          payload.append("link", content);
        } else {
          payload.append("content", content);
        }
      }

      images.forEach((image) => payload.append("images", image));
      if (isEditMode) {
        payload.append("imagesToKeep", JSON.stringify(oldImages));
        payload.append("imagesToDelete", JSON.stringify(imagesToDelete));
      }
    } else {
      // Use JSON for non-media content (Location, Mobile, Email, etc.)
      payload = {};
      if (entries.content === "google map") {
        payload = { location: title, link: content };
      } else if (entries.content === "link to phone") {
        payload = { mobile: title, link: content };
      } else if (entries.content === "link to email") {
        payload = { email: title, link: content };
      } else if (entries.content === "statics text") {
        payload = { title, content };
      } else {
        payload = { title, content };
      }
    }

    try {
      const config = useFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {};
      
      if (isEditMode) {
        await axios.put(`${apiEndPoint}/${currentData.id}`, payload, config);
        alert("Updated successfully!");
      } else {
        await axios.post(apiEndPoint, payload, config);
        alert("Added successfully!");
      }
      
      if (onCancel) onCancel();
      if (!isEditMode) {
        setTitle("");
        setContent("");
        setJoinUs("");
        setImages([]);
        setImagePreviews([]);
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      alert("An error occurred during submission.");
    }
  };

  return (
    <div className="bg-white rounded-3 shadow-sm p-4">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-4 gap-3">
        <h5 className="fw-bold m-0">{isEditMode ? `Edit ${entries.heading}` : `Add New ${entries.heading}`}</h5>
        {isEditMode && (
          <ButtonForm onclick={onCancel} buttonText="Cancel" color="btn-secondary" />
        )}
      </div>

      <form onSubmit={handleSubmit} className="row g-3">
        {entries.title && (
          <div className="col-12">
            <label className="form-label fw-semibold">{entries.title}</label>
            <input
              type="text"
              className="form-control"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`Enter ${entries.title}`}
            />
          </div>
        )}

        {entries.buttons && (
          <div className="col-12">
            <label className="form-label fw-semibold">{entries.buttons}</label>
            <input
              type="text"
              className="form-control"
              value={joinUs}
              onChange={(e) => setJoinUs(e.target.value)}
              placeholder={`Enter ${entries.buttons}`}
            />
          </div>
        )}

        {entries.content && (
          <div className="col-12">
            <label className="form-label fw-semibold">{entries.content}</label>
            {["news", "Home body", "doctors description", "service description"].includes(entries.content) ? (
              <RichTextForm value={content} onchange={setContent} />
            ) : (
              <textarea
                className="form-control"
                rows="4"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={`Enter ${entries.content}`}
              ></textarea>
            )}
          </div>
        )}

        {entries.photo && (
          <div className="col-12">
            <label className="form-label fw-semibold">Media (Images/Files)</label>
            <div className="border border-dashed rounded-3 p-4 text-center bg-light pointer position-relative">
              <i className="bi bi-cloud-arrow-up fs-2" style={{ color: "var(--primary-color)" }}></i>
              <p className="mb-0">Click or drag to upload {entries.photo}</p>
              <input
                type="file"
                multiple
                className="position-absolute top-0 start-0 w-100 h-100 opacity-0 pointer"
                onChange={handleImageChange}
              />
            </div>

            {oldImages.length > 0 && (
              <div className="mt-4">
                <p className="small fw-bold text-muted mb-2">
                  <i className="bi bi-info-circle me-1"></i>
                  Existing Media (Drag the handle <i className="bi bi-grip-vertical"></i> to reorder)
                </p>
                <DragDropContext onDragEnd={onDragEndImages}>
                  <Droppable droppableId="old-images-list" direction="horizontal">
                    {(provided) => (
                      <div 
                        className="d-flex flex-wrap gap-3 p-2 bg-light rounded-3"
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={{ minHeight: "110px" }}
                      >
                        {Array.isArray(oldImages) && oldImages.map((img, idx) => (
                          <Draggable key={img} draggableId={img} index={idx}>
                            {(provided, snapshot) => (
                              <div 
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`position-relative bg-white rounded shadow-sm border p-1 ${snapshot.isDragging ? "z-3 shadow-lg" : "z-1"}`}
                                style={{
                                  ...provided.draggableProps.style,
                                  opacity: snapshot.isDragging ? 0.9 : 1,
                                  border: snapshot.isDragging ? "2px solid var(--primary-color)" : "1px solid #dee2e6",
                                }}
                              >
                                {/* High-priority Drag Handle */}
                                <div 
                                  {...provided.dragHandleProps}
                                  className="position-absolute top-0 start-0 p-2 bg-white rounded-bottom-end border-end border-bottom shadow-sm d-flex align-items-center justify-content-center"
                                  style={{ cursor: "grab", zIndex: 30, minWidth: "24px", minHeight: "24px" }}
                                >
                                  <i className="bi bi-grip-vertical text-muted"></i>
                                </div>

                                <img 
                                  src={getImageUrl(img)} 
                                  alt="old" 
                                  className="rounded" 
                                  style={{ width: "80px", height: "80px", objectFit: "cover" }} 
                                />

                                <button 
                                  type="button" 
                                  className="btn btn-danger btn-sm position-absolute top-0 end-0 py-0 px-1 m-1 shadow-sm" 
                                  style={{ zIndex: 10 }}
                                  onClick={() => handleOldImageDelete(img)}
                                >
                                  <i className="bi bi-x"></i>
                                </button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            )}

            {imagePreviews.length > 0 && (
              <div className="mt-3">
                <p className="small fw-bold text-muted mb-2">New Media Previews:</p>
                <div className="d-flex flex-wrap gap-3 p-2 bg-white border rounded-3">
                  {imagePreviews.map((preview, idx) => (
                    <div key={idx} className="position-relative bg-light rounded shadow-sm border p-1">
                      <img src={preview} alt="preview" className="rounded" style={{ width: "80px", height: "80px", objectFit: "cover" }} />
                      <button 
                        type="button" 
                        className="btn btn-danger btn-sm position-absolute top-0 end-0 py-0 px-1 m-1 shadow-sm" 
                        onClick={() => handleNewImageDelete(idx)}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="col-12 mt-4">
          <button type="submit" className="btn btn-primary w-100 py-2 fw-bold" style={{ backgroundColor: "var(--primary-color)", border: "none" }}>
            {isEditMode ? "Update Changes" : `Add ${entries.heading}`}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DashboardForm;
